import {
  appearanceOf,
  type Direction,
  getItem,
  bodyLayerOf,
  hairFeatureOf,
  isBaseLayerVisible,
  isVisibleSlot,
  type Layer,
  layerOrder,
  type Equipped,
} from '@shefira/shared';

/**
 * The dressed character drawn on a DOM canvas, from the very frames the game
 * draws: body, skin, tinted eyes and hair, then each worn overlay tinted with its
 * item's colour. Same layer order, same tints, so what the panel shows is
 * what everyone else sees.
 */

/** A frame of the packed atlas: source image and the rectangle to copy. */
export interface AtlasFrame {
  image: CanvasImageSource;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DollPreviewOptions {
  bodyKey: string;
  frame: (name: string) => AtlasFrame | null;
  /** Feature tints (skin, eyes, hair) as CSS colours. */
  featureTints: Partial<Record<Layer, string>>;
  hairStyle?: string;
  /** `male` or `female` (shared `BODY_SHAPES`). */
  body?: string;
  scale: number;
  /** Centre the drawn pixels in the canvas instead of keeping the frame's own placement. */
  centred?: boolean;
}

export class DollPreview {
  readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly scratch: HTMLCanvasElement;
  /** The dressed frame at 1:1, copied to the canvas once every layer is in. */
  private readonly composite: HTMLCanvasElement;
  private readonly options: DollPreviewOptions;

  constructor(options: DollPreviewOptions) {
    this.options = { ...options };
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'doll__preview';
    this.canvas.width = 32 * options.scale;
    this.canvas.height = 32 * options.scale;
    const context = this.canvas.getContext('2d');
    if (context === null) throw new Error('2D canvas unavailable');
    this.context = context;
    this.scratch = document.createElement('canvas');
    this.scratch.width = 32;
    this.scratch.height = 32;
    this.composite = document.createElement('canvas');
    this.composite.width = 32;
    this.composite.height = 32;
  }

  /** New hair or eye colours; the next `draw` uses them. */
  setFeatureTints(tints: Partial<Record<Layer, string>>): void {
    this.options.featureTints = { ...tints };
  }

  /**
   * Draw the character as dressed, at idle frame `frameIndex` (the rigs'
   * idle has four, so a preview can cycle them and an animated piece lives).
   * What shows is what `appearanceOf` decides: a cosmetic over a slot hides
   * the piece under it (ADR 0031).
   */
  draw(equipped: Equipped, direction: Direction = 'south', frameIndex = 0): void {
    const { context, options } = this;
    const composite = this.composite.getContext('2d');
    if (composite === null) return;
    composite.clearRect(0, 0, 32, 32);

    const appearance = appearanceOf(equipped, getItem);
    for (const layer of layerOrder(direction)) {
      const feature =
        layer === 'hair'
          ? hairFeatureOf(options.hairStyle ?? '')
          : layer === 'body' || layer === 'skin'
            ? bodyLayerOf(layer, options.body ?? '')
            : layer;
      let name = `${options.bodyKey}/${feature ?? layer}/idle/${direction}/${String(frameIndex)}`;
      let tint: string | undefined = options.featureTints[layer];
      if (isVisibleSlot(layer)) {
        const template = getItem(appearance[layer] ?? '');
        if (template?.sprite === undefined) continue;
        name = `${options.bodyKey}/${template.sprite}/idle/${direction}/${String(frameIndex)}`;
        tint = template.tint;
      } else if (
        !isBaseLayerVisible(layer, appearance) ||
        feature === null
      ) {
        continue;
      }
      // A layer with fewer idle frames than the body (a still piece) shows its first.
      const frame = options.frame(name) ?? options.frame(name.replace(/\/\d+$/, '/0'));
      if (frame === null) continue;
      this.paint(composite, frame, tint);
    }

    const { x, y } = options.centred === true ? this.centringOffset(composite) : { x: 0, y: 0 };
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const scale = options.scale;
    context.drawImage(this.composite, 0, 0, 32, 32, x * scale, y * scale, 32 * scale, 32 * scale);
  }

  /**
   * Whole-pixel shift that puts the opaque pixels' bounding box in the middle
   * of the frame: the body stands at the bottom of its 32 px canvas, which
   * leaves it looking high and off-centre alone in a frame.
   */
  private centringOffset(composite: CanvasRenderingContext2D): { x: number; y: number } {
    const { data } = composite.getImageData(0, 0, 32, 32);
    let left = 32;
    let right = -1;
    let top = 32;
    let bottom = -1;
    for (let py = 0; py < 32; py += 1) {
      for (let px = 0; px < 32; px += 1) {
        if ((data[(py * 32 + px) * 4 + 3] ?? 0) === 0) continue;
        left = Math.min(left, px);
        right = Math.max(right, px);
        top = Math.min(top, py);
        bottom = Math.max(bottom, py);
      }
    }
    if (right < 0) return { x: 0, y: 0 };
    return {
      x: Math.floor((32 - (right - left + 1)) / 2) - left,
      y: Math.floor((32 - (bottom - top + 1)) / 2) - top,
    };
  }

  /** Copy a frame onto the composite, multiplied by a tint when the layer has one. */
  private paint(
    target: CanvasRenderingContext2D,
    frame: AtlasFrame,
    tint: string | undefined,
  ): void {
    const scratch = this.scratch.getContext('2d');
    if (scratch === null) return;
    scratch.clearRect(0, 0, 32, 32);
    scratch.globalCompositeOperation = 'source-over';
    scratch.drawImage(frame.image, frame.x, frame.y, frame.width, frame.height, 0, 0, 32, 32);
    if (tint !== undefined) {
      // Multiply keeps the shading of a greyscale mask; destination-in
      // restores the frame's own transparency afterwards.
      scratch.globalCompositeOperation = 'multiply';
      scratch.fillStyle = tint;
      scratch.fillRect(0, 0, 32, 32);
      scratch.globalCompositeOperation = 'destination-in';
      scratch.drawImage(frame.image, frame.x, frame.y, frame.width, frame.height, 0, 0, 32, 32);
    }
    target.drawImage(this.scratch, 0, 0);
  }
}
